import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calculator, TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  Receipt, Percent, Users, Package, Zap, Wrench, Megaphone,
  Box, FileCheck, Sparkles, Shield, Coins, MoreHorizontal,
  Plus, Pencil, Trash2, X, Save, Calendar, Download,
  ArrowUpRight, ArrowDownRight, AlertTriangle, CheckCircle2,
  Building, BarChart3, PieChart, Banknote, CreditCard, ArrowRightLeft,
  ChevronLeft, ChevronRight, RefreshCw, Bell, XCircle, Info, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import ExportToolbar from '../components/accounting/ExportToolbar';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  rent: Building, salaries: Users, raw_materials: Package, utilities: Zap,
  equipment_maintenance: Wrench, marketing: Megaphone, packaging: Box,
  licenses: FileCheck, cleaning: Sparkles, insurance: Shield,
  petty_cash: Coins, other: MoreHorizontal,
};

const CATEGORY_COLORS = {
  rent: 'bg-blue-500/10 text-blue-600', salaries: 'bg-purple-500/10 text-purple-600',
  raw_materials: 'bg-orange-500/10 text-orange-600', utilities: 'bg-yellow-500/10 text-yellow-600',
  equipment_maintenance: 'bg-slate-500/10 text-slate-600', marketing: 'bg-pink-500/10 text-pink-600',
  packaging: 'bg-amber-500/10 text-amber-600', licenses: 'bg-teal-500/10 text-teal-600',
  cleaning: 'bg-cyan-500/10 text-cyan-600', insurance: 'bg-indigo-500/10 text-indigo-600',
  petty_cash: 'bg-emerald-500/10 text-emerald-600', other: 'bg-gray-500/10 text-gray-600',
};

// ===== FINANCIAL DASHBOARD TAB =====
function DashboardTab({ data, period, setPeriod, t, lang }) {
  if (!data) return <LoadingState />;
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cards = [
    { label: t('accounting.totalRevenue'), value: fmt(data.total_revenue), icon: TrendingUp, change: data.revenue_change, color: 'from-emerald-500 to-green-600' },
    { label: t('accounting.totalExpenses'), value: fmt(data.total_expenses), icon: TrendingDown, change: data.expense_change, color: 'from-red-500 to-rose-600', invertChange: true },
    { label: t('accounting.netProfit'), value: fmt(data.net_profit), icon: DollarSign, suffix: `${data.profit_margin}%`, color: data.net_profit >= 0 ? 'from-amber-500 to-orange-600' : 'from-red-500 to-rose-600' },
    { label: t('accounting.orderCount'), value: data.order_count, icon: ShoppingCart, suffix: `${t('accounting.avgOrderValue')}: ${fmt(data.avg_order_value)}`, color: 'from-blue-500 to-indigo-600' },
  ];

  const paymentData = data.revenue_by_payment || {};
  const totalPayment = (paymentData.cash || 0) + (paymentData.card || 0) + (paymentData.transfer || 0);

  return (
    <div className="space-y-6" data-testid="accounting-dashboard-tab">
      {/* Period Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {['today', 'week', 'month', 'year'].map(p => (
          <Button key={p} variant={period === p ? 'default' : 'outline'} size="sm"
            onClick={() => setPeriod(p)} data-testid={`period-${p}`}
            className={period === p ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0' : ''}>
            {t(`accounting.${p}`)}
          </Button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="relative overflow-hidden" data-testid={`summary-card-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{c.label}</p>
                  <p className="text-2xl font-bold text-foreground">{c.value} <span className="text-sm font-normal text-muted-foreground">{t('common.sar')}</span></p>
                  {c.change !== undefined && c.change !== 0 && (
                    <div className={`flex items-center gap-1 text-xs ${(c.invertChange ? c.change > 0 : c.change > 0) ? (c.invertChange ? 'text-red-500' : 'text-emerald-500') : (c.invertChange ? 'text-emerald-500' : 'text-red-500')}`}>
                      {c.change > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      <span>{Math.abs(c.change)}% {t('accounting.vsLastPeriod')}</span>
                    </div>
                  )}
                  {c.suffix && !c.change && <p className="text-xs text-muted-foreground">{c.suffix}</p>}
                </div>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center flex-shrink-0`}>
                  <c.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* KPIs + Revenue by Payment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* KPIs */}
        <Card data-testid="kpis-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-4">{t('accounting.kpis')}</h3>
            <div className="space-y-4">
              <KPIBar label={t('accounting.foodCostPct')} value={data.kpis?.food_cost_pct || 0} ideal="28-35%" color="bg-orange-500" max={50} />
              <KPIBar label={t('accounting.laborCostPct')} value={data.kpis?.labor_cost_pct || 0} ideal="25-35%" color="bg-purple-500" max={50} />
              <KPIBar label={t('accounting.primeCostPct')} value={data.kpis?.prime_cost_pct || 0} ideal="< 65%" color="bg-amber-500" max={80} alert={data.kpis?.prime_cost_pct > 65} />
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Payment */}
        <Card data-testid="revenue-payment-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-4">{t('accounting.revenueByPayment')}</h3>
            <div className="space-y-3">
              {[
                { key: 'cash', icon: Banknote, color: 'bg-emerald-500', label: t('accounting.cash') },
                { key: 'card', icon: CreditCard, color: 'bg-blue-500', label: t('accounting.card') },
                { key: 'transfer', icon: ArrowRightLeft, color: 'bg-purple-500', label: t('accounting.transfer') },
              ].map(pm => {
                const val = paymentData[pm.key] || 0;
                const pct = totalPayment > 0 ? (val / totalPayment * 100) : 0;
                return (
                  <div key={pm.key} className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg ${pm.color}/10 flex items-center justify-center`}>
                      <pm.icon className={`w-4 h-4 ${pm.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">{pm.label}</span>
                        <span className="font-medium text-foreground">{fmt(val)} {t('common.sar')}</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${pm.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-end">{pct.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t flex justify-between text-sm">
              <span className="text-muted-foreground">{t('accounting.vatCollected')}</span>
              <span className="font-semibold text-foreground">{fmt(data.total_vat_collected)} {t('common.sar')}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses by Category */}
      {Object.keys(data.expenses_by_category || {}).length > 0 && (
        <Card data-testid="expenses-category-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-4">{t('accounting.expensesByCategory')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(data.expenses_by_category).sort((a, b) => b[1].total - a[1].total).map(([cat, info]) => {
                const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
                return (
                  <div key={cat} className={`p-3 rounded-xl ${CATEGORY_COLORS[cat] || 'bg-gray-500/10 text-gray-600'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4" />
                      <span className="text-xs font-medium">{t(`accounting.${toCamelCase(cat)}`)}</span>
                    </div>
                    <p className="text-lg font-bold">{fmt(info.total)}</p>
                    <p className="text-xs opacity-70">{info.count} {info.count === 1 ? 'transaction' : 'transactions'}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPIBar({ label, value, ideal, color, max, alert }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <span className={`font-bold ${alert ? 'text-red-500' : 'text-foreground'}`}>{value}%</span>
          {alert && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
        </div>
      </div>
      <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${alert ? 'bg-red-500' : color} rounded-full transition-all`} style={{ width: `${Math.min((value / max) * 100, 100)}%` }} />
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{ideal ? `${label}: ${ideal}` : ''}</p>
    </div>
  );
}

// ===== EXPENSES TAB =====
function ExpensesTab({ branchId, token, t, lang }) {
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/accounting/expenses?branch_id=${branchId}&page=${page}&limit=20`;
      if (filterCat) url += `&category=${filterCat}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setExpenses(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { toast.error(t('common.failed')); }
    setLoading(false);
  }, [branchId, token, page, filterCat, filterStatus, t]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('accounting.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/api/accounting/expenses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('accounting.expenseDeleted'));
      fetchExpenses();
    } catch { toast.error(t('common.failed')); }
  };

  return (
    <div className="space-y-4" data-testid="accounting-expenses-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => { setEditItem(null); setShowForm(true); }} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="add-expense-btn">
          <Plus className="w-4 h-4 me-2" /> {t('accounting.addExpense')}
        </Button>
        <Select value={filterCat} onValueChange={v => { setFilterCat(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="filter-category"><SelectValue placeholder={t('accounting.category')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {Object.keys(CATEGORY_ICONS).map(c => <SelectItem key={c} value={c}>{t(`accounting.${toCamelCase(c)}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={v => { setFilterStatus(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-32" data-testid="filter-status"><SelectValue placeholder={t('accounting.status')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="paid">{t('accounting.paid')}</SelectItem>
            <SelectItem value="pending">{t('accounting.pending')}</SelectItem>
            <SelectItem value="due">{t('accounting.due')}</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="ms-auto">{total} {t('accounting.expenses')}</Badge>
      </div>

      {/* Expense Form Modal */}
      {showForm && (
        <ExpenseForm
          branchId={branchId} token={token} t={t} editItem={editItem}
          onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchExpenses(); }}
        />
      )}

      {/* Expenses List */}
      {loading ? <LoadingState /> : expenses.length === 0 ? (
        <EmptyState icon={Receipt} title={t('accounting.noExpenses')} subtitle={t('accounting.noExpensesDesc')} />
      ) : (
        <div className="space-y-2">
          {expenses.map(exp => {
            const Icon = CATEGORY_ICONS[exp.category] || MoreHorizontal;
            return (
              <Card key={exp.id} className="hover:shadow-md transition-shadow" data-testid={`expense-row-${exp.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${CATEGORY_COLORS[exp.category] || 'bg-gray-100'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-foreground text-sm">{t(`accounting.${toCamelCase(exp.category)}`)}</span>
                      <StatusBadge status={exp.status} t={t} />
                      {exp.is_recurring && <Badge variant="outline" className="text-xs py-0 px-1.5">{t('accounting.recurring')}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{exp.description || exp.vendor_name || '-'}</p>
                  </div>
                  <div className="text-end flex-shrink-0">
                    <p className="font-bold text-foreground">{fmt(exp.amount)} <span className="text-xs font-normal text-muted-foreground">{t('common.sar')}</span></p>
                    <p className="text-xs text-muted-foreground">{exp.date}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditItem(exp); setShowForm(true); }} data-testid={`edit-expense-${exp.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => handleDelete(exp.id)} data-testid={`delete-expense-${exp.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="flex items-center text-sm text-muted-foreground px-3">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== EXPENSE FORM =====
function ExpenseForm({ branchId, token, t, editItem, onClose, onSaved }) {
  const [form, setForm] = useState({
    category: editItem?.category || 'raw_materials',
    amount: editItem?.amount || '',
    description: editItem?.description || '',
    date: editItem?.date || new Date().toISOString().split('T')[0],
    payment_method: editItem?.payment_method || 'cash',
    status: editItem?.status || 'paid',
    is_recurring: editItem?.is_recurring || false,
    recurring_frequency: editItem?.recurring_frequency || '',
    vendor_name: editItem?.vendor_name || '',
    vat_amount: editItem?.vat_amount || '',
    reference_number: editItem?.reference_number || '',
    notes: editItem?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error(t('accounting.amount')); return; }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/api/accounting/expenses/${editItem.id}`, { ...form, amount: parseFloat(form.amount), vat_amount: parseFloat(form.vat_amount || 0) }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t('accounting.expenseUpdated'));
      } else {
        await axios.post(`${API}/api/accounting/expenses`, { ...form, branch_id: branchId, amount: parseFloat(form.amount), vat_amount: parseFloat(form.vat_amount || 0) }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t('accounting.expenseAdded'));
      }
      onSaved();
    } catch { toast.error(t('common.failed')); }
    setSaving(false);
  };

  const Field = ({ label, children }) => (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="expense-form-modal">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground">{editItem ? t('accounting.editExpense') : t('accounting.addExpense')}</h2>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('accounting.category')}>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(CATEGORY_ICONS).map(c => <SelectItem key={c} value={c}>{t(`accounting.${toCamelCase(c)}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={`${t('accounting.amount')} (${t('common.sar')})`}>
              <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" data-testid="expense-amount" />
            </Field>
          </div>

          <Field label={t('accounting.description')}>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="expense-description" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('accounting.date')}>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="expense-date" />
            </Field>
            <Field label={t('accounting.paymentMethod')}>
              <Select value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
                <SelectTrigger data-testid="expense-payment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('accounting.cash')}</SelectItem>
                  <SelectItem value="card">{t('accounting.card')}</SelectItem>
                  <SelectItem value="transfer">{t('accounting.transfer')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('accounting.status')}>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="expense-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">{t('accounting.paid')}</SelectItem>
                  <SelectItem value="pending">{t('accounting.pending')}</SelectItem>
                  <SelectItem value="due">{t('accounting.due')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('accounting.vendor')}>
              <Input value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))} data-testid="expense-vendor" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('accounting.reference')}>
              <Input value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} data-testid="expense-reference" />
            </Field>
            <Field label={t('ledger.vatInput')}>
              <div className="flex gap-1.5">
                <Input type="number" value={form.vat_amount} onChange={e => setForm(f => ({ ...f, vat_amount: e.target.value }))} placeholder="0.00" data-testid="expense-vat" />
                <Button type="button" variant="outline" size="sm" className="px-2 text-xs whitespace-nowrap h-9"
                  onClick={() => { const amt = parseFloat(form.amount || 0); if (amt > 0) setForm(f => ({ ...f, vat_amount: (amt * 0.15 / 1.15).toFixed(2) })); }}
                  data-testid="expense-vat-auto">15%</Button>
              </div>
            </Field>
          </div>

          {/* Recurring toggle */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
            <input type="checkbox" checked={form.is_recurring} onChange={e => setForm(f => ({ ...f, is_recurring: e.target.checked }))} className="w-4 h-4 accent-amber-500" data-testid="expense-recurring" />
            <span className="text-sm text-foreground">{t('accounting.recurring')}</span>
            {form.is_recurring && (
              <Select value={form.recurring_frequency} onValueChange={v => setForm(f => ({ ...f, recurring_frequency: v }))}>
                <SelectTrigger className="w-28" data-testid="expense-freq"><SelectValue placeholder={t('accounting.recurringFreq')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{t('accounting.monthly')}</SelectItem>
                  <SelectItem value="weekly">{t('accounting.weekly')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <Field label={t('accounting.notes')}>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="expense-notes" />
          </Field>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="save-expense-btn">
              <Save className="w-4 h-4 me-2" /> {editItem ? t('common.update') : t('common.save')}
            </Button>
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== P&L TAB =====
function PnLTab({ branchId, token, t, lang }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/accounting/pnl?branch_id=${branchId}&period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(d);
      } catch { toast.error(t('common.failed')); }
      setLoading(false);
    };
    fetch();
  }, [branchId, token, period, t]);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/api/accounting/export/pnl?branch_id=${branchId}&period=${period}&format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pnl_report_${period}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(t('common.failed')); }
  };

  if (loading) return <LoadingState />;
  if (!data) return null;

  const maxVal = Math.max(...(data.daily_data || []).map(d => Math.max(d.revenue, d.expenses)), 1);

  return (
    <div className="space-y-6" data-testid="accounting-pnl-tab">
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {['today', 'week', 'month', 'year'].map(p => (
          <Button key={p} variant={period === p ? 'default' : 'outline'} size="sm" onClick={() => setPeriod(p)}
            className={period === p ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0' : ''}>
            {t(`accounting.${p}`)}
          </Button>
        ))}
        <Button variant="outline" size="sm" className="ms-auto" onClick={handleExport} data-testid="export-csv-btn">
          <Download className="w-4 h-4 me-2" /> {t('accounting.exportCSV')}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: t('accounting.totalRevenue'), value: data.summary.total_revenue, color: 'text-emerald-500' },
          { label: t('accounting.totalExpenses'), value: data.summary.total_expenses, color: 'text-red-500' },
          { label: t('accounting.netProfit'), value: data.summary.net_profit, color: data.summary.net_profit >= 0 ? 'text-amber-500' : 'text-red-500' },
          { label: t('accounting.profitMargin'), value: `${data.summary.profit_margin}%`, isText: true, color: data.summary.profit_margin >= 0 ? 'text-emerald-500' : 'text-red-500' },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.isText ? s.value : `${fmt(s.value)}`}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart - Bar chart using div-based visualization */}
      {data.daily_data && data.daily_data.length > 0 && (
        <Card data-testid="pnl-chart">
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-4">{t('accounting.revenueVsExpenses')}</h3>
            <div className="flex items-end gap-1 h-48 overflow-x-auto pb-2">
              {data.daily_data.map((d, i) => (
                <div key={i} className="flex flex-col items-center gap-1 min-w-[28px] flex-1" title={`${d.date}: ${t('accounting.revenue')} ${fmt(d.revenue)} | ${t('accounting.expensesLabel')} ${fmt(d.expenses)}`}>
                  <div className="flex gap-0.5 items-end w-full h-36">
                    <div className="flex-1 bg-emerald-500/80 rounded-t transition-all" style={{ height: `${(d.revenue / maxVal) * 100}%` }} />
                    <div className="flex-1 bg-red-400/80 rounded-t transition-all" style={{ height: `${(d.expenses / maxVal) * 100}%` }} />
                  </div>
                  <span className="text-[9px] text-muted-foreground whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 justify-center text-xs">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500/80" />{t('accounting.revenue')}</div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-400/80" />{t('accounting.expensesLabel')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Items + Expenses Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.revenue_by_item && data.revenue_by_item.length > 0 && (
          <Card data-testid="top-items-card">
            <CardContent className="p-5">
              <h3 className="font-semibold text-foreground mb-3">{t('accounting.topItems')}</h3>
              <div className="space-y-2">
                {data.revenue_by_item.slice(0, 10).map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-5">{i + 1}.</span>
                      <span className="text-foreground">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">{item.qty}x</span>
                      <span className="font-medium text-foreground">{fmt(item.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {data.expenses_by_category && data.expenses_by_category.length > 0 && (
          <Card data-testid="pnl-expenses-breakdown">
            <CardContent className="p-5">
              <h3 className="font-semibold text-foreground mb-3">{t('accounting.expensesByCategory')}</h3>
              <div className="space-y-2">
                {data.expenses_by_category.map((e, i) => {
                  const Icon = CATEGORY_ICONS[e.category] || MoreHorizontal;
                  return (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-foreground">{t(`accounting.${toCamelCase(e.category)}`)}</span>
                      </div>
                      <span className="font-medium text-foreground">{fmt(e.total)} {t('common.sar')}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ===== FOOD COST TAB =====
function FoodCostTab({ branchId, token, t, lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [costInput, setCostInput] = useState('');
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/accounting/food-cost?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(d);
    } catch { toast.error(t('common.failed')); }
    setLoading(false);
  }, [branchId, token, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateCost = async (itemId) => {
    try {
      await axios.put(`${API}/api/accounting/food-cost/${itemId}`, { cost_price: parseFloat(costInput) || 0 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('accounting.costUpdated'));
      setEditingId(null);
      fetchData();
    } catch { toast.error(t('common.failed')); }
  };

  if (loading) return <LoadingState />;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="accounting-foodcost-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.totalRevenue')}</p>
          <p className="text-xl font-bold text-emerald-500">{fmt(data.summary.total_revenue)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.costPrice')}</p>
          <p className="text-xl font-bold text-red-500">{fmt(data.summary.total_cost)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.overallFoodCost')}</p>
          <p className={`text-xl font-bold ${data.summary.overall_food_cost_pct > 35 ? 'text-red-500' : 'text-amber-500'}`}>{data.summary.overall_food_cost_pct}%</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.itemsWithAlert')}</p>
          <p className={`text-xl font-bold ${data.summary.items_with_alert > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{data.summary.items_with_alert}</p>
        </CardContent></Card>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="food-cost-table">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="p-3 text-start font-medium text-muted-foreground">{t('accounting.description')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.sellPrice')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.costPrice')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.margin')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.foodCostPctItem')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.qtySold')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.totalProfit')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item.id} className={`border-b hover:bg-muted/20 ${item.alert ? 'bg-red-500/5' : ''}`} data-testid={`food-cost-row-${item.id}`}>
                  <td className="p-3">
                    <div>
                      <span className="font-medium text-foreground">{lang === 'ar' && item.name_ar ? item.name_ar : item.name}</span>
                      <span className="text-xs text-muted-foreground block">{item.category}</span>
                    </div>
                  </td>
                  <td className="p-3 text-center font-medium">{fmt(item.sell_price)}</td>
                  <td className="p-3 text-center">
                    {editingId === item.id ? (
                      <div className="flex items-center gap-1 justify-center">
                        <Input type="number" className="w-20 h-7 text-center text-sm" value={costInput} onChange={e => setCostInput(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleUpdateCost(item.id)} />
                        <Button size="icon" className="h-7 w-7" onClick={() => handleUpdateCost(item.id)}><CheckCircle2 className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}><X className="w-3 h-3" /></Button>
                      </div>
                    ) : (
                      <button className="text-foreground hover:text-amber-500 transition-colors" onClick={() => { setEditingId(item.id); setCostInput(String(item.cost_price || '')); }} data-testid={`edit-cost-${item.id}`}>
                        {item.cost_price > 0 ? fmt(item.cost_price) : <span className="text-muted-foreground text-xs">{t('accounting.noCostSet')}</span>}
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-center font-medium">{item.cost_price > 0 ? fmt(item.margin) : '-'}</td>
                  <td className="p-3 text-center">
                    {item.cost_price > 0 ? (
                      <span className={`font-bold ${item.alert ? 'text-red-500' : item.food_cost_pct <= 28 ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {item.food_cost_pct}%
                        {item.alert && <AlertTriangle className="w-3 h-3 inline ms-1" />}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="p-3 text-center text-muted-foreground">{item.qty_sold}</td>
                  <td className="p-3 text-center font-medium">{item.total_profit > 0 ? <span className="text-emerald-500">{fmt(item.total_profit)}</span> : '-'}</td>
                  <td className="p-3 text-center">
                    {item.alert && <Badge className="bg-red-500/10 text-red-500 text-xs">{t('accounting.highCostAlert')}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== CASH MANAGEMENT TAB =====
function CashMgmtTab({ branchId, token, t, lang }) {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/accounting/cash-management?branch_id=${branchId}&date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(d);
      } catch { toast.error(t('common.failed')); }
      setLoading(false);
    };
    fetch();
  }, [branchId, token, date, t]);

  if (loading) return <LoadingState />;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="accounting-cash-tab">
      {/* Date picker */}
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="cash-date-picker" />
      </div>

      {/* Cash Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: t('accounting.openingCash'), value: data.opening_cash, icon: Banknote, color: 'text-blue-500' },
          { label: t('accounting.cashSales'), value: data.cash_sales, icon: TrendingUp, color: 'text-emerald-500' },
          { label: t('accounting.expectedCash'), value: data.expected_cash, icon: Calculator, color: 'text-amber-500' },
          { label: t('accounting.netCash'), value: data.net_cash, icon: DollarSign, color: 'text-foreground' },
        ].map((c, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <c.icon className={`w-4 h-4 ${c.color}`} />
                <span className="text-xs text-muted-foreground">{c.label}</span>
              </div>
              <p className={`text-xl font-bold ${c.color}`}>{fmt(c.value)} <span className="text-xs font-normal text-muted-foreground">{t('common.sar')}</span></p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Cash Difference Alert */}
      {data.actual_cash > 0 && data.cash_difference !== 0 && (
        <Card className={data.cash_difference > 0 ? 'border-emerald-500/30' : 'border-red-500/30'}>
          <CardContent className="p-4 flex items-center gap-3">
            {data.cash_difference > 0 ? <ArrowUpRight className="w-5 h-5 text-emerald-500" /> : <ArrowDownRight className="w-5 h-5 text-red-500" />}
            <div>
              <span className="text-sm text-muted-foreground">{t('accounting.difference')}: </span>
              <span className={`font-bold ${data.cash_difference > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {data.cash_difference > 0 ? '+' : ''}{fmt(data.cash_difference)} {t('common.sar')}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shifts */}
      {data.shifts.length === 0 ? (
        <EmptyState icon={Banknote} title={t('accounting.noShifts')} />
      ) : (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-3">{t('accounting.cashMgmt')} - {date}</h3>
            <div className="space-y-3">
              {data.shifts.map((s, i) => (
                <div key={i} className="p-3 rounded-xl bg-muted/30 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="text-sm font-medium text-foreground">{s.cashier_name || 'Cashier'}</span>
                    <span className={`ms-2 text-xs px-2 py-0.5 rounded-full ${s.status === 'open' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-gray-500/10 text-gray-500'}`}>{s.status}</span>
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>{t('accounting.openingCash')}: {fmt(s.opening_cash)}</span>
                    <span>{t('accounting.cashSales')}: {fmt(s.cash_sales)}</span>
                    {s.status === 'closed' && <span>{t('accounting.actualCash')}: {fmt(s.actual_cash)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Petty Cash */}
      {data.petty_cash_expenses.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-3">{t('accounting.pettyCashExpenses')}</h3>
            <div className="space-y-2">
              {data.petty_cash_expenses.map((e, i) => {
                const Icon = CATEGORY_ICONS[e.category] || Coins;
                return (
                  <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span>{t(`accounting.${toCamelCase(e.category)}`)}</span>
                      {e.description && <span className="text-muted-foreground">- {e.description}</span>}
                    </div>
                    <span className="font-medium text-red-500">-{fmt(e.amount)}</span>
                  </div>
                );
              })}
              <div className="border-t pt-2 flex justify-between font-semibold text-sm">
                <span>{t('accounting.pettyCashExpenses')}</span>
                <span className="text-red-500">-{fmt(data.total_petty_cash)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== VAT REPORT TAB =====
function VATReportTab({ branchId, token, t, lang }) {
  const now = new Date();
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/accounting/vat-report?branch_id=${branchId}&quarter=${quarter}&year=${year}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(d);
      } catch { toast.error(t('common.failed')); }
      setLoading(false);
    };
    fetch();
  }, [branchId, token, quarter, year, t]);

  if (loading) return <LoadingState />;
  if (!data) return null;

  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthsAr = ['', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

  return (
    <div className="space-y-6" data-testid="accounting-vat-tab">
      {/* Quarter Selector */}
      <div className="flex items-center gap-3 flex-wrap">
        {[1, 2, 3, 4].map(q => (
          <Button key={q} variant={quarter === q ? 'default' : 'outline'} size="sm" onClick={() => setQuarter(q)}
            className={quarter === q ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0' : ''} data-testid={`quarter-${q}`}>
            {t(`accounting.q${q}`)}
          </Button>
        ))}
        <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.taxableSales')}</p>
          <p className="text-xl font-bold text-foreground">{fmt(data.taxable_sales)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.vatCollected')} ({(data.vat_rate * 100)}%)</p>
          <p className="text-xl font-bold text-amber-500">{fmt(data.vat_collected)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.orderCount')}</p>
          <p className="text-xl font-bold text-foreground">{data.order_count}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">{t('accounting.invoiceCount')}</p>
          <p className="text-xl font-bold text-foreground">{data.invoice_count}</p>
        </CardContent></Card>
      </div>

      {/* Monthly Breakdown */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-foreground mb-4">{t('accounting.monthlyBreakdown')}</h3>
          <table className="w-full text-sm" data-testid="vat-monthly-table">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="p-3 text-start font-medium text-muted-foreground">{t('accounting.month')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.taxableSales')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.vatCollected')}</th>
                <th className="p-3 text-center font-medium text-muted-foreground">{t('accounting.orderCount')}</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly_breakdown.map((m, i) => (
                <tr key={i} className="border-b hover:bg-muted/20">
                  <td className="p-3 font-medium">{lang === 'ar' ? monthsAr[m.month] : months[m.month]} {year}</td>
                  <td className="p-3 text-center">{fmt(m.taxable_sales)}</td>
                  <td className="p-3 text-center font-medium text-amber-500">{fmt(m.vat_collected)}</td>
                  <td className="p-3 text-center">{m.order_count}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-muted/30 font-bold">
                <td className="p-3">{t('common.all')}</td>
                <td className="p-3 text-center">{fmt(data.taxable_sales)}</td>
                <td className="p-3 text-center text-amber-500">{fmt(data.vat_collected)}</td>
                <td className="p-3 text-center">{data.order_count}</td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== HELPER COMPONENTS =====
function LoadingState() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center py-16 space-y-3" data-testid="empty-state">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-muted/50 flex items-center justify-center">
        <Icon className="w-8 h-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground max-w-sm mx-auto">{subtitle}</p>}
    </div>
  );
}

function StatusBadge({ status, t }) {
  const styles = {
    paid: 'bg-emerald-500/10 text-emerald-600',
    pending: 'bg-amber-500/10 text-amber-600',
    due: 'bg-red-500/10 text-red-600',
  };
  return <Badge className={`text-xs py-0 px-1.5 ${styles[status] || 'bg-gray-500/10'}`}>{t(`accounting.${status}`)}</Badge>;
}

function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

// ===== ALERTS PANEL =====
function AlertsPanel({ branchId, token, t, lang }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('zouqai_dismissed_alerts') || '[]'); } catch { return []; }
  });
  const [expanded, setExpanded] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    if (!branchId) return;
    const fetchAlerts = async () => {
      setLoading(false);
      try {
        const { data } = await axios.get(`${API}/api/accounting/alerts?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } });
        setAlerts(data.alerts || []);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 120000);
    return () => clearInterval(interval);
  }, [branchId, token]);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem('zouqai_dismissed_alerts', JSON.stringify(next));
  };

  const active = alerts.filter(a => !dismissed.includes(a.id));
  if (loading || active.length === 0) return null;

  const ALERT_STYLES = {
    danger: { bg: 'bg-red-500/8 border-red-500/20', icon: ShieldAlert, iconColor: 'text-red-500', badge: 'bg-red-500/10 text-red-600' },
    warning: { bg: 'bg-amber-500/8 border-amber-500/20', icon: AlertTriangle, iconColor: 'text-amber-500', badge: 'bg-amber-500/10 text-amber-600' },
    info: { bg: 'bg-blue-500/8 border-blue-500/20', icon: Info, iconColor: 'text-blue-500', badge: 'bg-blue-500/10 text-blue-600' },
  };

  return (
    <div className="space-y-3" data-testid="alerts-panel">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 w-full group" data-testid="alerts-toggle">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          <h2 className="font-semibold text-foreground">{t('alerts.title')}</h2>
          <Badge className="bg-red-500 text-white text-xs px-1.5 py-0">{active.length}</Badge>
        </div>
        <ChevronLeft className={`w-4 h-4 text-muted-foreground ms-auto transition-transform ${expanded ? 'rotate-[-90deg]' : 'rotate-0'}`} />
      </button>

      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {active.map(alert => {
            const style = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
            const Icon = style.icon;
            const data = alert.data || {};

            let message = t(alert.message_key, data);
            // Fallback if i18next doesn't interpolate
            if (message === alert.message_key) {
              message = alert.message_key;
            }

            return (
              <Card key={alert.id} className={`border ${style.bg} relative overflow-hidden`} data-testid={`alert-${alert.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${style.badge}`}>
                      <Icon className={`w-4.5 h-4.5 ${style.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-foreground">{t(alert.title_key)}</h4>
                        <Badge className={`text-[10px] py-0 px-1 ${style.badge}`}>{t(`alerts.${alert.type}`)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{message}</p>

                      {/* Extra details for specific alerts */}
                      {alert.id === 'food_cost_high' && data.items && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {data.items.map((item, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">
                              {item.name} ({item.pct}%)
                            </span>
                          ))}
                        </div>
                      )}

                      {alert.id === 'recurring_due' && data.items && (
                        <div className="mt-2 space-y-1">
                          {data.items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-[10px]">
                              <span className="text-muted-foreground">{t(`accounting.${toCamelCase(item.category)}`)}</span>
                              <span className="font-medium">{fmt(item.amount)} {t('common.sar')}</span>
                              <Badge variant="outline" className="text-[9px] py-0 px-1">
                                {item.days_until === 0 ? t('alerts.dueToday') : t('alerts.daysUntil', { days: item.days_until })}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={() => dismiss(alert.id)} className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 p-0.5" data-testid={`dismiss-alert-${alert.id}`}>
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== MAIN PAGE =====
export default function AccountingPage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [tab, setTab] = useState('dashboard');
  const [dashData, setDashData] = useState(null);
  const [period, setPeriod] = useState('month');

  const branchId = activeBranch?.id;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!branchId) return;
    const fetchDashboard = async () => {
      try {
        const { data } = await axios.get(`${API}/api/accounting/dashboard?branch_id=${branchId}&period=${period}`, { headers });
        setDashData(data);
      } catch (e) { /* silent */ }
    };
    if (tab === 'dashboard') fetchDashboard();
  }, [branchId, period, tab, token]);

  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const buildReport = useCallback(() => {
    if (tab === 'dashboard' && dashData) {
      const d = dashData;
      const kpis = [
        { label: t('accounting.totalRevenue'), value: fmt(d.total_revenue), unit: t('common.sar'), highlight: 'positive' },
        { label: t('accounting.totalExpenses'), value: fmt(d.total_expenses), unit: t('common.sar'), highlight: 'negative' },
        { label: t('accounting.netProfit'), value: fmt(d.net_profit), unit: t('common.sar'), highlight: d.net_profit >= 0 ? 'positive' : 'negative' },
        { label: t('accounting.orderCount'), value: String(d.order_count || 0) },
      ];
      const sections = [];

      // KPIs section
      if (d.kpis) {
        sections.push({
          title: t('accounting.kpis'),
          type: 'kpis',
          items: [
            { label: t('accounting.foodCostPct'), value: `${d.kpis.food_cost_pct || 0}%`, highlight: d.kpis.food_cost_pct > 35 ? 'negative' : '' },
            { label: t('accounting.laborCostPct'), value: `${d.kpis.labor_cost_pct || 0}%` },
            { label: t('accounting.primeCostPct'), value: `${d.kpis.prime_cost_pct || 0}%`, highlight: d.kpis.prime_cost_pct > 65 ? 'negative' : '' },
          ],
        });
      }

      // Revenue by payment
      const pm = d.revenue_by_payment || {};
      if (pm.cash || pm.card || pm.transfer) {
        sections.push({
          title: t('accounting.revenueByPayment'),
          type: 'table',
          headers: [t('accounting.paymentMethod'), t('accounting.amount'), '%'],
          rows: [
            { cells: [{ value: t('accounting.cash') }, { value: fmt(pm.cash || 0), type: 'num' }, { value: `${((pm.cash || 0) / ((pm.cash || 0) + (pm.card || 0) + (pm.transfer || 0)) * 100 || 0).toFixed(1)}%`, type: 'num' }] },
            { cells: [{ value: t('accounting.card') }, { value: fmt(pm.card || 0), type: 'num' }, { value: `${((pm.card || 0) / ((pm.cash || 0) + (pm.card || 0) + (pm.transfer || 0)) * 100 || 0).toFixed(1)}%`, type: 'num' }] },
            { cells: [{ value: t('accounting.transfer') }, { value: fmt(pm.transfer || 0), type: 'num' }, { value: `${((pm.transfer || 0) / ((pm.cash || 0) + (pm.card || 0) + (pm.transfer || 0)) * 100 || 0).toFixed(1)}%`, type: 'num' }] },
          ],
        });
      }

      // Expenses by category
      if (d.expenses_by_category && Object.keys(d.expenses_by_category).length > 0) {
        sections.push({
          title: t('accounting.expensesByCategory'),
          type: 'table',
          headers: [t('accounting.category'), t('accounting.amount'), t('accounting.count')],
          rows: Object.entries(d.expenses_by_category).sort((a, b) => b[1].total - a[1].total).map(([cat, info]) => ({
            cells: [{ value: t(`accounting.${toCamelCase(cat)}`) }, { value: `${fmt(info.total)} ${t('common.sar')}`, type: 'num' }, { value: String(info.count), type: 'num' }],
          })),
        });
      }

      return { kpis, sections };
    }
    return null;
  }, [tab, dashData, t, lang, period]);

  const csvData = tab === 'dashboard' && dashData?.expenses_by_category ? {
    columns: [
      { key: 'category', label: t('accounting.category') },
      { key: 'total', label: t('accounting.amount') },
      { key: 'count', label: t('accounting.count') },
    ],
    data: Object.entries(dashData.expenses_by_category).map(([cat, info]) => ({
      category: t(`accounting.${toCamelCase(cat)}`),
      total: info.total,
      count: info.count,
    })),
  } : null;

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-6" data-testid="accounting-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('accounting.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{activeBranch?.name || ''}</p>
          </div>
          <ExportToolbar title={t('accounting.title')} subtitle={activeBranch?.name} buildReport={buildReport} csvData={csvData} t={t} lang={lang} />
        </div>

        {/* Smart Alerts */}
        <AlertsPanel branchId={branchId} token={token} t={t} lang={lang} />

        {/* Tabs - reduced to 5 core tabs */}
        <div>
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="w-full grid grid-cols-5 h-auto p-1 bg-muted/50" data-testid="accounting-tabs">
              <TabsTrigger value="dashboard" className="text-xs sm:text-sm py-2" data-testid="tab-dashboard">
                <BarChart3 className="w-4 h-4 me-1.5 hidden sm:block" />{t('accounting.dashboard')}
              </TabsTrigger>
              <TabsTrigger value="expenses" className="text-xs sm:text-sm py-2" data-testid="tab-expenses">
                <Receipt className="w-4 h-4 me-1.5 hidden sm:block" />{t('accounting.expenses')}
              </TabsTrigger>
              <TabsTrigger value="pnl" className="text-xs sm:text-sm py-2" data-testid="tab-pnl">
                <TrendingUp className="w-4 h-4 me-1.5 hidden sm:block" />{t('accounting.pnl')}
              </TabsTrigger>
              <TabsTrigger value="foodcost" className="text-xs sm:text-sm py-2" data-testid="tab-foodcost">
                <Package className="w-4 h-4 me-1.5 hidden sm:block" />{t('accounting.foodCost')}
              </TabsTrigger>
              <TabsTrigger value="vat" className="text-xs sm:text-sm py-2" data-testid="tab-vat">
                <Percent className="w-4 h-4 me-1.5 hidden sm:block" />{t('accounting.vatReport')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard">
              <DashboardTab data={dashData} period={period} setPeriod={setPeriod} t={t} lang={lang} />
            </TabsContent>
            <TabsContent value="expenses">
              <ExpensesTab branchId={branchId} token={token} t={t} lang={lang} />
            </TabsContent>
            <TabsContent value="pnl">
              <PnLTab branchId={branchId} token={token} t={t} lang={lang} />
            </TabsContent>
            <TabsContent value="foodcost">
              <FoodCostTab branchId={branchId} token={token} t={t} lang={lang} />
            </TabsContent>
            <TabsContent value="vat">
              <VATReportTab branchId={branchId} token={token} t={t} lang={lang} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </ModuleLayout>
  );
}
